import { DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgbDatepickerModule, NgbDropdownModule, NgbModule, NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleModule } from '@ng-idle/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TagInputModule } from 'ngx-chips';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Const } from './core/const/const';
import { HttpErrorInterceptorService } from './core/interceptors/http-error-interceptor.service';
import { RegulatedInterceptor } from './core/interceptors/regulated.interceptor';
import { FooterComponent } from './shared/components/footer/footer.component';
import { AdminComponent } from './shared/components/nav/admin.component';
import { NavBarComponent } from './shared/components/nav/nav-bar/nav-bar.component';
import { NavRightComponent } from './shared/components/nav/nav-bar/nav-right/nav-right.component';
import { NavCollapseComponent } from './shared/components/nav/navigation/nav-content/nav-collapse/nav-collapse.component';
import { NavContentComponent } from './shared/components/nav/navigation/nav-content/nav-content.component';
import { NavGroupComponent } from './shared/components/nav/navigation/nav-content/nav-group/nav-group.component';
import { NavItemComponent } from './shared/components/nav/navigation/nav-content/nav-item/nav-item.component';
import { NavigationItem } from './shared/components/nav/navigation/navigation';
import { NavigationComponent } from './shared/components/nav/navigation/navigation.component';
import { ErrorHandlerDirective } from './shared/directives/error-handler.directive';
import { ManageTransactionsDirective } from './shared/directives/manage-transactions.directive';
import { CommonHelper } from './shared/helpers/common-helper';
import { SharedModule } from './shared/shared.module';
import { UpdateStripComponent } from './shared/components/update-strip/update-strip.component';
import { TelegramErrorReporterService } from './core/services/telegram-error-reporter.service';
import { SwUpdateNavigationService } from './core/services/sw-update-navigation.service';
import { TelegramErrorInterceptor } from './core/interceptors/telegram-error.interceptor';
import { TelegramErrorHandler } from './core/services/telegram-error-handler';
import { TrackedToastrService } from './core/services/tracked-toastr.service';
import { SwProcessingInterceptor } from './core/interceptors/sw-processing.interceptor';
import { ServiceWorkerBypassInterceptor } from './core/interceptors/service-worker-bypass.interceptor';


/**
 * Builds the APP_INITIALIZER that attaches the window-level error listeners.
 * @param reporter the Telegram alert reporter
 * @returns the initializer
 */
function installErrorListeners(reporter: TelegramErrorReporterService): () => void {
    return () => reporter.installGlobalListeners();
}
/**
 * Builds the APP_INITIALIZER that applies a pending new-build update on the next page navigation.
 * @param updateNavigation the navigation-time updater
 * @returns the initializer
 */
function startUpdateNavigation(updateNavigation: SwUpdateNavigationService): () => void {
    return () => updateNavigation.start();
}

@NgModule({ declarations: [
        AppComponent,
        AdminComponent,
        NavigationComponent,
        NavContentComponent,
        NavGroupComponent,
        NavCollapseComponent,
        NavItemComponent,
        NavBarComponent,
        NavRightComponent,
        FooterComponent,
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [SharedModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        // CardComponent,
        NgbDropdownModule,
        NgbTooltipModule,
        NgbModule,
        ToastrModule.forRoot(),
        TagInputModule,
        ReactiveFormsModule,
        FormsModule,
        NgbDatepickerModule,
        NgSelectModule,
        ClipboardModule,
        NgbPaginationModule,
        NgxSkeletonLoaderModule.forRoot({ animation: 'pulse' }),
        NgIdleModule.forRoot(),
        // On for the deployed builds only (development/staging/pre-production/production);
        // off on the local `ng serve`, where its caching would fight development.
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            // Register once the app is stable, or after 30 seconds, whichever comes first.
            registrationStrategy: 'registerWhenStable:30000'
        }),
        UpdateStripComponent], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: RegulatedInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptorService, multi: true },
        // Report-only: failed backend calls go to the ops Telegram channel; the error is rethrown unchanged.
        { provide: HTTP_INTERCEPTORS, useClass: TelegramErrorInterceptor, multi: true },
        // Uncaught errors keep Angular's default console output and are also alerted.
        { provide: ErrorHandler, useClass: TelegramErrorHandler },
        // Window-level error/rejection listeners, installed before any feature code runs.
        { provide: APP_INITIALIZER, useFactory: installErrorListeners, deps: [TelegramErrorReporterService], multi: true },
        // Every `toastr.error(...)` in the app is mirrored to the alert channel. Overriding
        // the root ToastrService covers all existing call sites without editing any of them.
        { provide: ToastrService, useClass: TrackedToastrService },
        // Counts API calls so a new-build update never reloads the page mid-request.
        { provide: HTTP_INTERCEPTORS, useClass: SwProcessingInterceptor, multi: true },
        // Innermost: tags FormData uploads with ?ngsw-bypass so iOS Safari's service worker cannot drop the file body.
        { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkerBypassInterceptor, multi: true },
        // A pending new build is applied on the next page navigation (see SwUpdateNavigationService).
        { provide: APP_INITIALIZER, useFactory: startUpdateNavigation, deps: [SwUpdateNavigationService], multi: true },
        NavigationItem, CommonHelper, Const, DecimalPipe, ManageTransactionsDirective, ErrorHandlerDirective,
        provideHttpClient(withInterceptorsFromDi())
    ] })
/**
 * Export Class
 */
export class AppModule {

}
